import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, PatientRegisterRequest } from '../../services/api';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import FormField, { Input, Checkbox } from '../ui/FormField';
import StatusBanner from '../ui/StatusBanner';
import {
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  PhoneIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const PatientRegister: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const redirectQuery = redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : '';
  const [formData, setFormData] = useState<Omit<PatientRegisterRequest, 'tos_accepted'>>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    consent_given: false,
  });
  const [tosAccepted, setTosAccepted] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Password strength checks
  const passwordStrength = {
    hasMinLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
  };
  const isPasswordStrong = Object.values(passwordStrength).every(Boolean);

  const validateForm = (): string | null => {
    if (!isPasswordStrong) {
      return 'Password does not meet requirements';
    }
    if (formData.password !== confirmPassword) {
      return 'Passwords do not match';
    }
    if (!formData.consent_given) {
      return 'You must consent to the privacy policy to register';
    }
    if (!tosAccepted) {
      return 'You must accept the Terms of Service to register';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      await authAPI.registerPatient({
        ...formData,
        tos_accepted: tosAccepted,
      });
      toast.success('Registration successful! Please log in.');
      navigate(`/login${redirectQuery}`);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg).join(', '));
      } else {
        setError(detail || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Brand Visual */}
      <div className="hidden lg:flex lg:w-5/12 bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-clinic-primary/5 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-clinic-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-clinic-gold/10 rounded-full translate-y-1/2 -translate-x-1/2" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16">
          {/* Logo */}
          <img
            src="/logos/logo-primary.png"
            alt="Clinic Studios"
            className="h-14 w-auto mb-10 object-contain object-left"
          />

          {/* Tagline */}
          <h1 className="text-3xl lg:text-4xl font-bold text-clinic-dark font-body leading-tight mb-6">
            Your wellness
            <br />
            <span className="text-clinic-primary">journey starts here</span>
          </h1>

          <p className="text-base text-clinic-dark/70 max-w-sm mb-8 leading-relaxed">
            Create your patient account to connect with trusted mental health professionals.
          </p>

          {/* Benefits */}
          <div className="space-y-3">
            {[
              'Easy online booking',
              'Secure & confidential',
              'Flexible scheduling',
              'Direct clinician access',
            ].map((feature, index) => (
              <div key={index} className="flex items-center text-clinic-dark/70">
                <CheckCircleIcon className="w-5 h-5 text-clinic-primary mr-3" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom decoration */}
        <div className="absolute bottom-8 left-12 lg:left-16">
          <img
            src="/logos/logo-icon.png"
            alt=""
            className="h-8 w-auto opacity-30"
          />
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="w-full lg:w-7/12 flex items-center justify-center px-6 py-8 bg-white">
        <div className="w-full max-w-lg">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-6 text-center">
            <img
              src="/logos/logo-primary.png"
              alt="Clinic Studios"
              className="h-10 w-auto mx-auto mb-2"
            />
          </div>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-clinic-dark font-body">
              Patient Registration
            </h2>
            <p className="mt-1 text-clinic-muted text-sm">
              Create your account to book appointments
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4">
              <StatusBanner
                variant="error"
                title="Registration Error"
                message={error}
                dismissible
                onDismiss={() => setError('')}
              />
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="First Name" htmlFor="first_name" required>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  autoComplete="given-name"
                  placeholder="First name"
                  maxLength={100}
                  value={formData.first_name}
                  onChange={handleChange}
                  leftIcon={<UserIcon className="h-5 w-5" />}
                  required
                />
              </FormField>
              <FormField label="Last Name" htmlFor="last_name" required>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  autoComplete="family-name"
                  placeholder="Last name"
                  maxLength={100}
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </FormField>
            </div>

            <FormField label="Email Address" htmlFor="email" required>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                leftIcon={<EnvelopeIcon className="h-5 w-5" />}
                required
              />
            </FormField>

            <FormField label="Phone Number" htmlFor="phone">
              <Input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                placeholder="(555) 123-4567"
                maxLength={20}
                value={formData.phone}
                onChange={handleChange}
                leftIcon={<PhoneIcon className="h-5 w-5" />}
              />
            </FormField>

            <FormField label="Password" htmlFor="password" required>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                placeholder="Create a password"
                minLength={8}
                value={formData.password}
                onChange={handleChange}
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                required
              />
              {/* Password Strength Indicators */}
              {formData.password && (
                <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                  <span className={passwordStrength.hasMinLength ? 'text-clinic-primary' : 'text-clinic-muted'}>
                    {passwordStrength.hasMinLength ? '✓' : '○'} 8+ characters
                  </span>
                  <span className={passwordStrength.hasUppercase ? 'text-clinic-primary' : 'text-clinic-muted'}>
                    {passwordStrength.hasUppercase ? '✓' : '○'} Uppercase
                  </span>
                  <span className={passwordStrength.hasLowercase ? 'text-clinic-primary' : 'text-clinic-muted'}>
                    {passwordStrength.hasLowercase ? '✓' : '○'} Lowercase
                  </span>
                  <span className={passwordStrength.hasNumber ? 'text-clinic-primary' : 'text-clinic-muted'}>
                    {passwordStrength.hasNumber ? '✓' : '○'} Number
                  </span>
                </div>
              )}
            </FormField>

            <FormField label="Confirm Password" htmlFor="confirmPassword" required>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                error={Boolean(confirmPassword && formData.password !== confirmPassword)}
                required
              />
            </FormField>

            {/* PIPEDA Consent */}
            <div className="bg-clinic-cream/50 p-4 rounded-card border border-clinic-primary-lighter">
              <Checkbox
                id="consent_given"
                name="consent_given"
                checked={formData.consent_given}
                onChange={handleChange}
                label={
                  <span className="text-sm text-clinic-dark">
                    <strong>Privacy Policy Consent</strong>
                    <p className="font-normal text-clinic-muted mt-1">
                      I consent to Clinic Studios collecting and using my personal information
                      in accordance with the{' '}
                      <Link to="/privacy" target="_blank" className="text-clinic-primary hover:underline">
                        Privacy Policy
                      </Link>
                      . I understand my information will be used to manage my appointments and communications with my practitioner.
                    </p>
                  </span>
                }
                required
              />
            </div>

            {/* Terms of Service */}
            <div className="bg-clinic-cream/50 p-4 rounded-card border border-clinic-primary-lighter">
              <Checkbox
                id="tos_accepted"
                name="tos_accepted"
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                label={
                  <span className="text-sm text-clinic-dark">
                    <strong>Terms of Service</strong>
                    <p className="font-normal text-clinic-muted mt-1">
                      I have read and agree to the{' '}
                      <Link to="/terms" target="_blank" className="text-clinic-primary hover:underline">
                        Terms of Service
                      </Link>
                      , including the booking policies and cancellation terms.
                    </p>
                  </span>
                }
                required
              />
            </div>

            <Button
              type="submit"
              fullWidth
              size="lg"
              isLoading={isLoading}
              rightIcon={!isLoading && <ArrowRightIcon className="h-5 w-5" />}
            >
              {isLoading ? 'Creating Account...' : 'Create Client Account'}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-clinic-muted">
              Already have an account?{' '}
              <Link to={`/login${redirectQuery}`} className="text-clinic-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>
            <p className="text-sm text-clinic-muted">
              Are you a clinician?{' '}
              <Link to={`/register/clinician${redirectQuery}`} className="text-clinic-primary hover:underline font-medium">
                Register as a clinician
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientRegister;
